/**
 * Os atendimentos desta organização (spec 157, FR-012, FR-013).
 *
 * **COMO A PERGUNTA "TEM O MÓDULO?" É RESPONDIDA, e por que não há flag.** Não
 * existe campo "tem o módulo": o que existe é a lista de agentes de atendimento,
 * derivada do que o atendimento de fato usa — um vínculo de caixa de entrada ou
 * um documento de agente publicado (R-11). A barra lateral e o editor do agente
 * perguntam ao mesmo endereço, e por isso não discordam do servidor.
 *
 * Enquanto a resposta não chega, `temModulo` é `null` — nem sim nem não. Quem
 * renderiza precisa saber a diferença entre "não tem" e "ainda não sei", ou a
 * barra lateral pisca o grupo Atendimento a cada carregamento.
 */
import { useCallback, useEffect, useState } from 'react'
import { atendimentoService } from '@/services/painel'
import type { Atendimento } from '@/schemas/painel'

export interface EstadoDoAtendimento {
  /** Uma linha por atendimento (agente, projeto, caixas, versão no ar). */
  agentes: Atendimento[]
  temModulo: boolean | null
  carregando: boolean
  recarregar: () => Promise<void>
  /** O atendimento de um agente, se ele for um. */
  atendimentoDe: (agentId?: string | null) => Atendimento | null
}

export function useAtendimento(organizationId?: string | null): EstadoDoAtendimento {
  const [agentes, setAgentes] = useState<Atendimento[]>([])
  const [temModulo, setTemModulo] = useState<boolean | null>(null)
  const [carregando, setCarregando] = useState(false)

  const buscar = useCallback(async () => {
    if (!organizationId) {
      setAgentes([])
      setTemModulo(null)
      return
    }
    setCarregando(true)
    try {
      const lista = await atendimentoService.agentes(organizationId)
      setAgentes(lista)
      setTemModulo(lista.length > 0)
    } catch {
      // SEM MÓDULO É O PADRÃO SEGURO. Falha de rede aqui esconde o grupo
      // Atendimento — mostrar telas que vão dar erro é pior que não mostrar.
      setAgentes([])
      setTemModulo(false)
    } finally {
      setCarregando(false)
    }
  }, [organizationId])

  useEffect(() => {
    void buscar()
  }, [buscar])

  const atendimentoDe = useCallback(
    (agentId?: string | null) =>
      agentId ? (agentes.find((a) => a.agent_id === agentId) ?? null) : null,
    [agentes]
  )

  return { agentes, temModulo, carregando, recarregar: buscar, atendimentoDe }
}
